import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../config";
import {
  RiCheckboxCircleLine,
  RiFileTextLine,
  RiCalendarLine,
  RiMapPinLine,
  RiUserStarLine,
  RiMegaphoneLine,
  RiArrowRightLine,
  RiDownloadLine,
  RiPrinterLine,
  RiShareLine,
  RiMailLine,
  RiWhatsappLine,
  RiLinkedinLine,
  RiTwitterXLine,
  RiFacebookLine,
  RiShieldCheckLine,
  RiHeartLine,
  RiGlobalLine,
  RiTeamLine,
} from "react-icons/ri";
import toast from "react-hot-toast";

/* ─── THEME COLORS (Blue & White) ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F8FAFC",
  softBlue: "#EFF6FF",
  primary: "#1A56DB",
  primaryDark: "#1240A8",
  primaryLight: "#3B82F6",
  navy: "#0F2557",
  navyDeep: "#091A3E",
  accent: "#60A5FA",
  accentGold: "#F59E0B",
  text: "#1E293B",
  textMid: "#475569",
  textMuted: "#64748B",
  border: "#E2E8F0",
  borderLight: "#F1F5F9",
  shadow: "0 4px 20px rgba(0,0,0,0.05)",
  shadowMd: "0 8px 30px rgba(0,0,0,0.08)",
  shadowLg: "0 20px 40px rgba(0,0,0,0.1)",
  gradPrimary: "linear-gradient(135deg, #1A56DB 0%, #3B82F6 100%)",
  gradNavy: "linear-gradient(135deg, #0F2557 0%, #1A56DB 100%)",
  gradSoft: "linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 100%)",
  gradGold: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
};

const GuidelinesPage = () => {
  const [guidelines, setGuidelines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedItem, setExpandedItem] = useState(null);

  useEffect(() => {
    fetchGuidelines();
  }, []);

  const fetchGuidelines = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/guidelines/active`);
      if (data.success) {
        setGuidelines(data.data);
      }
    } catch (error) {
      console.error("Error fetching guidelines:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to load guidelines");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (platform, item) => {
    const text = `${item.title}: ${item.description}`;
    const url = window.location.href;
    
    const shareUrls = {
      email: `mailto:?subject=${encodeURIComponent(item.title)}&body=${encodeURIComponent(text)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], "_blank");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${guidelines?.title || "Conference Guidelines"}</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; }
          h1 { color: #1A56DB; }
          .guideline-item { margin-bottom: 20px; padding: 15px; background: #F4F7FF; border-radius: 10px; }
          hr { border: 1px solid #D6E4FF; }
        </style>
      </head>
      <body>
        <h1>${guidelines?.title || "Conference Guidelines"}</h1>
        <p>${guidelines?.description || ""}</p>
        <hr/>
        ${guidelines?.guidelines?.map(item => `
          <div class="guideline-item">
            <h3>${item.title}</h3>
            <p>${item.description}</p>
          </div>
        `).join('')}
        <hr/>
        <p><small>Generated on ${new Date().toLocaleDateString()}</small></p>
      </body>
      </html>
    `;
    
    const blob = new Blob([content], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${guidelines?.title || "guidelines"}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Guidelines downloaded!");
  };

  const categories = guidelines?.guidelines 
    ? [...new Set(guidelines.guidelines.map(g => g.icon || "📌"))]
    : [];

  const filteredGuidelines = guidelines?.guidelines?.filter(item => 
    selectedCategory === "all" || (item.icon || "📌") === selectedCategory
  ) || [];

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: T.offWhite, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{ textAlign: "center" }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            style={{ display: "inline-block" }}
          >
            <RiFileTextLine size={48} color={T.primary} />
          </motion.div>
          <p style={{ marginTop: "1rem", color: T.textMid, fontWeight: 500 }}>Loading Guidelines...</p>
        </motion.div>
      </div>
    );
  }

  if (!guidelines) {
    return (
      <div style={{ minHeight: "100vh", background: T.offWhite, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: "400px", padding: "2rem" }}>
          <RiFileTextLine size={64} color={T.textMuted} />
          <h2 style={{ color: T.text, marginTop: "1rem" }}>No Guidelines Available</h2>
          <p style={{ color: T.textMuted }}>Guidelines will be posted soon. Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: T.offWhite, fontFamily: "'Inter', sans-serif" }}>
      {/* Font Import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>

   

      {/* Hero Section */}
      <div style={{ 
        background: T.gradNavy, 
        position: "relative", 
        overflow: "hidden",
        padding: "80px 20px 100px",
      }}>
        <div style={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          background: `radial-gradient(circle, ${T.primaryLight}20, transparent)`,
          borderRadius: "50%",
          animation: "float 6s ease-in-out infinite",
        }} />
        <div style={{
          position: "absolute",
          bottom: -80,
          left: -80,
          width: 250,
          height: 250,
          background: `radial-gradient(circle, ${T.accent}20, transparent)`,
          borderRadius: "50%",
          animation: "float 8s ease-in-out infinite reverse",
        }} />
        
        <div style={{ maxWidth: "1200px", margin: "0 auto", position: "relative", zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div style={{ textAlign: "center" }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                  background: `${T.white}20`,
                  backdropFilter: "blur(10px)",
                  padding: "8px 20px",
                  borderRadius: "50px",
                  marginBottom: "24px",
                }}
              >
                <RiCheckboxCircleLine color={T.accent} size={20} />
                <span style={{ color: T.white, fontWeight: 500 }}>Official Guidelines</span>
              </motion.div>
              
              <h1 style={{
                fontSize: "clamp(2rem, 5vw, 3.5rem)",
                fontWeight: 900,
                color: T.white,
                marginBottom: "16px",
                fontFamily: "'Playfair Display', serif",
              }}>
                {guidelines.title}
              </h1>
              
              <p style={{
                fontSize: "clamp(1rem, 2vw, 1.2rem)",
                color: T.accent,
                maxWidth: "600px",
                margin: "0 auto",
              }}>
                {guidelines.description}
              </p>
              
              {/* Stats */}
              <div style={{
                display: "flex",
                justifyContent: "center",
                gap: "40px",
                marginTop: "40px",
                flexWrap: "wrap",
              }}>
                <div>
                  <div style={{ fontSize: "32px", fontWeight: 800, color: T.white }}>{guidelines.guidelines?.length || 0}</div>
                  <div style={{ color: `${T.accent}`, fontSize: "14px", fontWeight: 500 }}>Guidelines</div>
                </div>
                <div>
                  <div style={{ fontSize: "32px", fontWeight: 800, color: T.white }}>100%</div>
                  <div style={{ color: `${T.accent}`, fontSize: "14px", fontWeight: 500 }}>Compliance</div>
                </div>
                <div>
                  <div style={{ fontSize: "32px", fontWeight: 800, color: T.white }}>24/7</div>
                  <div style={{ color: `${T.accent}`, fontSize: "14px", fontWeight: 500 }}>Support</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: "1200px", margin: "-40px auto 0", padding: "0 20px 60px", position: "relative", zIndex: 3 }}>
        
        {/* Action Bar */}
        <motion.div
          className="no-print"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            background: T.white,
            borderRadius: "20px",
            padding: "20px 30px",
            boxShadow: T.shadowMd,
            marginBottom: "40px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
            <button
              onClick={handleDownload}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 20px",
                background: T.gradPrimary,
                color: T.white,
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: 600,
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              <RiDownloadLine size={18} /> Download
            </button>
            <button
              onClick={handlePrint}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 20px",
                background: T.softBlue,
                color: T.primary,
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: 600,
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = T.borderLight}
              onMouseLeave={(e) => e.currentTarget.style.background = T.softBlue}
            >
              <RiPrinterLine size={18} /> Print
            </button>
          </div>
          
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <span style={{ color: T.textMuted, fontSize: "14px", fontWeight: 500 }}>Share:</span>
            <button onClick={() => handleShare("email", guidelines)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, fontSize: "18px", transition: "color 0.2s" }} onMouseEnter={e => e.currentTarget.style.color = T.primary} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}><RiMailLine /></button>
            <button onClick={() => handleShare("whatsapp", guidelines)} style={{ background: "none", border: "none", cursor: "pointer", color: "#25D366", fontSize: "18px", transition: "opacity 0.2s" }} onMouseEnter={e => e.currentTarget.style.opacity = "0.8"} onMouseLeave={e => e.currentTarget.style.opacity = "1"}><RiWhatsappLine /></button>
            <button onClick={() => handleShare("linkedin", guidelines)} style={{ background: "none", border: "none", cursor: "pointer", color: "#0077B5", fontSize: "18px", transition: "opacity 0.2s" }} onMouseEnter={e => e.currentTarget.style.opacity = "0.8"} onMouseLeave={e => e.currentTarget.style.opacity = "1"}><RiLinkedinLine /></button>
            <button onClick={() => handleShare("twitter", guidelines)} style={{ background: "none", border: "none", cursor: "pointer", color: "#000000", fontSize: "18px", transition: "opacity 0.2s" }} onMouseEnter={e => e.currentTarget.style.opacity = "0.8"} onMouseLeave={e => e.currentTarget.style.opacity = "1"}><RiTwitterXLine /></button>
            <button onClick={() => handleShare("facebook", guidelines)} style={{ background: "none", border: "none", cursor: "pointer", color: "#1877F2", fontSize: "18px", transition: "opacity 0.2s" }} onMouseEnter={e => e.currentTarget.style.opacity = "0.8"} onMouseLeave={e => e.currentTarget.style.opacity = "1"}><RiFacebookLine /></button>
          </div>
        </motion.div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <motion.div
            className="no-print"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "40px",
              justifyContent: "center",
            }}
          >
            <button
              onClick={() => setSelectedCategory("all")}
              style={{
                padding: "10px 24px",
                background: selectedCategory === "all" ? T.gradPrimary : T.white,
                color: selectedCategory === "all" ? T.white : T.text,
                border: `1px solid ${selectedCategory === "all" ? "transparent" : T.borderLight}`,
                borderRadius: "50px",
                cursor: "pointer",
                fontWeight: 600,
                transition: "all 0.3s",
                boxShadow: selectedCategory === "all" ? T.shadow : "none",
              }}
            >
              All Guidelines
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  padding: "10px 24px",
                  background: selectedCategory === cat ? T.gradPrimary : T.white,
                  color: selectedCategory === cat ? T.white : T.text,
                  border: `1px solid ${selectedCategory === cat ? "transparent" : T.borderLight}`,
                  borderRadius: "50px",
                  cursor: "pointer",
                  fontWeight: 600,
                  transition: "all 0.3s",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: selectedCategory === cat ? T.shadow : "none",
                }}
              >
                <span>{cat}</span>
              </button>
            ))}
          </motion.div>
        )}

        {/* Guidelines Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", gap: "24px" }}>
          <AnimatePresence>
            {filteredGuidelines.sort((a, b) => a.order - b.order).map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -8 }}
                style={{
                  background: T.white,
                  borderRadius: "24px",
                  overflow: "hidden",
                  boxShadow: T.shadow,
                  cursor: "pointer",
                  transition: "all 0.3s",
                }}
                onClick={() => setExpandedItem(expandedItem === index ? null : index)}
              >
                <div style={{ padding: "28px", position: "relative" }}>
                  {/* Icon and Number */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
                    <div style={{
                      width: "60px",
                      height: "60px",
                      background: T.softBlue,
                      borderRadius: "18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "30px",
                    }}>
                      {item.icon || "📌"}
                    </div>
                    <span style={{
                      background: T.softBlue,
                      padding: "4px 12px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: T.primary,
                    }}>
                      #{index + 1}
                    </span>
                  </div>
                  
                  {/* Title */}
                  <h3 style={{
                    fontSize: "1.3rem",
                    fontWeight: 800,
                    color: T.text,
                    marginBottom: "12px",
                    lineHeight: 1.3,
                    fontFamily: "'Playfair Display', serif",
                  }}>
                    {item.title}
                  </h3>
                  
                  {/* Description */}
                  <p style={{
                    color: T.textMid,
                    lineHeight: 1.6,
                    marginBottom: "20px",
                  }}>
                    {expandedItem === index ? item.description : `${item.description.substring(0, 120)}${item.description.length > 120 ? "..." : ""}`}
                  </p>
                  
                  {/* Read More Link */}
                  {item.description.length > 120 && (
                    <div style={{ color: T.primary, fontSize: "14px", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
                      {expandedItem === index ? "Read Less" : "Read More"} <RiArrowRightLine size={14} style={{ transition: "transform 0.3s", transform: expandedItem === index ? "rotate(90deg)" : "none" }} />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* No Results */}
        {filteredGuidelines.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              textAlign: "center",
              padding: "60px",
              background: T.white,
              borderRadius: "24px",
              boxShadow: T.shadowMd,
            }}
          >
            <RiFileTextLine size={48} color={T.textMuted} />
            <p style={{ color: T.textMuted, marginTop: "16px" }}>No guidelines found for this category.</p>
          </motion.div>
        )}

        {/* Footer Note */}
        
      </div>

     
    </div>
  );
};

export default GuidelinesPage;